'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowRight, Sparkles, Phone, Clock, CheckCircle2, Shield, GraduationCap, Star } from 'lucide-react';
import CounsellingModal from '@/components/CounsellingModal';

const TRUST_SIGNALS = [
  { icon: CheckCircle2, label: '52,000+ Students Counselled', color: 'text-success' },
  { icon: Shield, label: 'MCC Verified Counsellors', color: 'text-[#1a3a8f]' },
  { icon: Star, label: '4.9★ Student Rating', color: 'text-warning' },
  { icon: Clock, label: 'Same-Day Appointments', color: 'text-[#1a3a8f]' },
];

export default function CTABanner() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <CounsellingModal isOpen={modalOpen} onClose={() => setModalOpen(false)} source="CTA Banner" />
      <section className="py-20">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8">
          <div className="relative glass-card rounded-3xl p-10 sm:p-16 text-center overflow-hidden border border-[#1a3a8f]/15">
            {/* Background orbs */}
            <div className="floating-orb w-80 h-80 bg-[#1a3a8f]/10 -top-20 -left-20" />
            <div className="floating-orb w-60 h-60 bg-[#1a3a8f]/8 -bottom-10 -right-10" />

            {/* Grid */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(26,58,143,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(26,58,143,0.03)_1px,transparent_1px)] bg-[size:40px_40px]" />

            <div className="relative z-10">
              {/* Urgency badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-success/10 border border-success/20 rounded-full text-sm text-success font-semibold mb-4">
                <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
                <span>NEET 2026 Counselling Open — Book your session today</span>
              </div>

              <div className="inline-flex items-center gap-2 px-4 py-2 glass-card rounded-full text-sm text-[#1a3a8f] border border-[#1a3a8f]/20 mb-6 ml-3">
                <Sparkles size={14} />
                <span>Free First Counselling Session</span>
              </div>

              <h2 className="text-4xl sm:text-5xl font-extrabold text-foreground mb-4 tracking-tight">
                Don&apos;t Navigate <span className="gradient-text">Admissions Alone</span>
              </h2>
              <p className="text-muted-foreground text-lg max-w-xl mx-auto mb-8">
                52,000+ students trusted Wikas Education counsellors to secure their dream medical college. 
                Book your free session now — expert guidance from application to admission.
              </p>

              {/* Trust signals strip */}
              <div className="flex flex-wrap items-center justify-center gap-4 mb-10">
                {TRUST_SIGNALS?.map((ts) => (
                  <div key={ts?.label} className="flex items-center gap-2 px-3 py-2 bg-white/70 rounded-xl border border-slate-100 shadow-sm">
                    <ts.icon size={14} className={ts?.color} />
                    <span className="text-xs font-semibold text-foreground">{ts?.label}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={() => setModalOpen(true)}
                  className="btn-primary flex items-center gap-2 px-8 py-4 text-base w-full sm:w-auto"
                >
                  <Phone size={18} />
                  Book Free Counselling
                  <ArrowRight size={18} />
                </button>
                <Link href="/neet-college-predictor" className="btn-secondary flex items-center gap-2 px-8 py-4 text-base w-full sm:w-auto">
                  <GraduationCap size={16} />
                  Try NEET Predictor
                </Link>
              </div>

              <p className="text-xs text-muted-foreground mt-6">
                No credit card required · Free first session · Expert NEET counsellors
              </p>
            </div>
          </div>

          {/* Why Choose Wikas */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                title: 'vs Self-Research',
                points: ['Expert counsellor guidance', 'Avoid costly mistakes', 'Save weeks of research'],
                highlight: false,
              },
              {
                title: 'Wikas Advantage',
                points: ['Personalised admission strategy', 'End-to-end counselling support', 'AI predictor + human expertise'],
                highlight: true,
              },
              {
                title: 'vs Generic Platforms',
                points: ['Real counsellors, not bots', 'Round-wise strategy', 'Document & verification help'],
                highlight: false,
              },
            ]?.map((col) => (
              <div
                key={col?.title}
                className={`rounded-2xl p-5 border text-center ${
                  col?.highlight
                    ? 'bg-[#0a1f5c] text-white border-[#0a1f5c] shadow-lg shadow-[#0a1f5c]/20 scale-105'
                    : 'glass-card-white border-slate-200'
                }`}
              >
                <p className={`text-sm font-bold mb-3 ${col?.highlight ? 'text-white' : 'text-foreground'}`}>{col?.title}</p>
                <ul className="space-y-2">
                  {col?.points?.map((pt) => (
                    <li key={pt} className={`text-xs flex items-center gap-2 justify-center ${col?.highlight ? 'text-white/90' : 'text-muted-foreground'}`}>
                      <CheckCircle2 size={12} className={col?.highlight ? 'text-blue-300' : 'text-success'} />
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}